import * as React from 'react';
import { useSnackbar } from 'notistack';
import { cn } from '../../lib/utils';
import { CartaoIndicador, FiltroSegmentado, Num, OpcaoSegmento, CABECALHO_TABELA, IconePlanilha } from './index';
import { Skeleton } from '../ui/skeleton';
import { OPCOES_LINHAS, rotuloLinhas } from './paginacao';
import TituloTela from './TituloTela';
import CaixaBusca from './CaixaBusca';

/* ============================================================================
   PainelLista - o esqueleto de TODA tela de listagem no visual novo.

   Por que existe: as telas de Grupos, Politicas, APKs, Usuarios, Regionais e
   Aplicativos sao a mesma tela com colunas diferentes. Repetir cabecalho,
   indicadores, filtro, grade, estados de carga/erro/vazio e paginacao em cada
   uma garante que na terceira ja tenham divergido (foi o que aconteceu na versao
   MUI: seis copias do `InvoiceTableRow` com codigo morto identico dentro).

   Aqui a tela declara COLUNAS e ENTREGA DADOS. Todo o resto e igual por
   construcao - inclusive as tres coisas que quase sempre saem erradas:

   1. ERRO e VAZIO sao estados DIFERENTES. Lista vazia e informacao ("nao existe
      grupo com esse filtro"); falha de rede e outra coisa, e precisa de botao de
      tentar de novo. Misturar os dois faz o usuario concluir que o dado sumiu.

   2. O ESQUELETO usa a mesma grade das linhas, entao a tela nao "pula" quando os
      dados chegam.

   3. A ALTURA DA LINHA vem do token --gps-row-h, nunca de valor fixo: e o que faz
      o modo compacto ser um atributo no <html> e nao uma prop em cada tela.
   ============================================================================ */

export type Coluna<T> = {
  chave: string;
  rotulo: string;
  /** Trecho da grade CSS, ex.: 'minmax(200px,2.4fr)' ou '96px'. */
  largura: string;
  alinhar?: 'esq' | 'dir';
  /** Largura do bloco cinza do esqueleto, ex.: 'w-40'. */
  esqueleto?: string;
  /**
   * Nome do campo de ordenacao que o BACKEND aceita para esta coluna.
   *
   * Presente = cabecalho clicavel. Ausente = cabecalho estatico.
   *
   * Nao inventar: cada endpoint interpola este valor na consulta, e nome
   * invalido ou e ignorado em silencio (resultado errado sem erro) ou derruba a
   * consulta. A tela antiga marcava `sort: true` em todas as colunas, inclusive
   * nas que o backend nao ordena - dezenas de cabecalhos clicaveis que nao
   * ordenavam nada.
   */
  ordenarPor?: string;
  /** Valor para a exportacao. Ausente = coluna fica fora do arquivo. */
  exportar?: (linha: T) => string | number | null | undefined;
  render: (linha: T, indice: number) => React.ReactNode;
};

/* Colunas da faixa por quantidade de indicadores. Precisa ser mapa de classes
   literais - ver o comentario no uso. Com 4 ou mais vira 2x2 em tela media, para
   nao apertar quatro numeros grandes na largura de um notebook. */
const COLUNAS_FAIXA: Record<number, string> = {
  0: '',
  1: '',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
  // Tickets tem 5 (Abertos, Em atendimento, Respondidos, SLA vencido, Resolvidos):
  // com o teto em 4 o quinto caia sozinho numa segunda linha.
  5: 'sm:grid-cols-3 md:grid-cols-5',
};

export type Indicador = {
  rotulo: string;
  valor: number;
  legenda?: string;
  tom?: 'info' | 'ok' | 'danger' | 'warn' | 'neutro';
  /** Base para a barra e para o "% da base". Ausente = sem barra. */
  base?: number;
};

export default function PainelLista<T>({
  titulo,
  descricao,
  acao,
  indicadores,
  busca,
  aoBuscar,
  placeholderBusca = 'Buscar',
  segmentos,
  segmentoAtual,
  aoTrocarSegmento,
  filtrosExtras,
  colunas,
  linhas,
  chaveLinha,
  aoClicarLinha,
  carregando,
  erro,
  aoRecarregar,
  vazio = 'Nenhum registro encontrado com esse filtro.',
  pagina,
  totalPaginas,
  total,
  aoTrocarPagina,
  rotuloPlural = 'registros',
  ordenarPor,
  direcao,
  aoOrdenar,
  linhasPorPagina,
  aoTrocarLinhasPorPagina,
  aoLimparFiltros,
  nomeArquivo,
}: {
  titulo: string;
  descricao?: React.ReactNode;
  acao?: React.ReactNode;
  indicadores?: Indicador[];
  busca?: string;
  aoBuscar?: (valor: string) => void;
  placeholderBusca?: string;
  segmentos?: Array<OpcaoSegmento<number>>;
  segmentoAtual?: number;
  aoTrocarSegmento?: (valor: number) => void;
  filtrosExtras?: React.ReactNode;
  colunas: Array<Coluna<T>>;
  linhas: T[];
  chaveLinha: (linha: T, indice: number) => string;
  aoClicarLinha?: (linha: T) => void;
  carregando?: boolean;
  erro?: string;
  aoRecarregar?: () => void;
  vazio?: string;
  pagina: number;
  totalPaginas: number;
  total: number;
  aoTrocarPagina: (pagina: number) => void;
  rotuloPlural?: string;
  /** Campo de ordenacao atual (o valor que vai para o backend). */
  ordenarPor?: string;
  direcao?: 'asc' | 'desc';
  aoOrdenar?: (campo: string) => void;
  linhasPorPagina?: number;
  aoTrocarLinhasPorPagina?: (n: number) => void;
  /** Presente = mostra o botao de limpar filtros. */
  aoLimparFiltros?: () => void;
  /** Presente = mostra o botao de exportar. Nome do arquivo, sem extensao. */
  nomeArquivo?: string;
}) {
  // Grade unica para cabecalho, esqueleto e linhas. E o que mantem as colunas
  // alinhadas sem <table> e permite a coluna numerica virar regua vertical.
  const grade = React.useMemo(() => colunas.map((c) => c.largura).join(' '), [colunas]);

  const [exportando, setExportando] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();

  /* BUSCA (29/09/2026, revista em 21/10/2026).
     Ate 21/10 o campo avisava a pagina 350 ms depois de parar de digitar - ainda
     uma consulta por pausa. Agora a CaixaBusca so chama `aoBuscar` no Enter ou na
     lupa (e no "x", sem filtro). As paginas seguem recebendo o termo pelo mesmo
     `aoBuscar`, sem mudar nada nelas. Ver CaixaBusca.tsx. */

  /**
   * Exporta as linhas da PAGINA ATUAL para .xlsx.
   *
   * Duas decisoes registradas:
   *
   * 1. So a pagina atual, e o rotulo diz isso. A tela antiga montava o arquivo a
   *    partir do mesmo `tableData` da pagina - ou seja, tambem exportava so o que
   *    estava carregado - mas o botao dizia apenas "Exportar", o que fazia
   *    parecer que vinha a base inteira. Exportar tudo exigiria uma consulta
   *    propria, sem paginacao, e isso e outra tarefa.
   *
   * 2. `xlsx` e `file-saver` entram por import DINAMICO, porque sao pesados e
   *    quase ninguem exporta. ATENCAO: hoje isso NAO reduz o bundle - o
   *    `utils/ExportCSV.jsx` das telas antigas importa `xlsx` de forma estatica e
   *    puxa a biblioteca para o chunk principal de qualquer jeito. O ganho so
   *    aparece quando as telas antigas sairem; verificado com
   *    `grep -l SheetNames dist-vite/assets/*.js`, que ainda aponta para o
   *    index principal.
   */
  async function exportar() {
    const colunasExportaveis = colunas.filter((c) => c.exportar);
    if (!colunasExportaveis.length || !linhas.length) return;

    setExportando(true);
    try {
      // A geracao do arquivo mora em utils/planilha - a MESMA rotina que o
      // BotaoExportar e as telas sem PainelLista usam. Antes havia quatro copias
      // dela no projeto, com larguras de coluna diferentes entre si.
      const { baixarPlanilha } = await import('../../utils/planilha');
      await baixarPlanilha(
        linhas.map((linha) => {
          const registro: Record<string, any> = {};
          colunasExportaveis.forEach((c) => {
            const v = c.exportar!(linha);
            registro[c.rotulo] = v === null || v === undefined ? '' : v;
          });
          return registro;
        }),
        nomeArquivo
      );
    } catch {
      // Sem catch, a falha virava so rejeicao no console e o botao voltava ao normal.
      enqueueSnackbar('Não foi possível gerar a planilha.', { variant: 'error' });
    } finally {
      setExportando(false);
    }
  }

  /* DENSIDADE: SEMPRE COMPACTA (19/08/2026, a pedido).
     Era um botao que alternava entre "Normal" e "Compacto" gravando
     `data-density` no <html>. Duas coisas contra ele: a escolha e sempre a mesma
     (linha baixa cabe mais dado na tela, que e o ponto de uma lista de 31 mil
     dispositivos), e um botao cujo rotulo e o ESTADO OPOSTO ao atual - dizia
     "Compacto" quando estava normal - obriga a pensar duas vezes para saber onde
     se esta.
     O atributo continua sendo escrito, porque e dele que os tokens de altura de
     linha dependem (`--gps-row-h`); o que saiu foi a escolha. */
  React.useEffect(() => {
    document.documentElement.setAttribute('data-density', 'compact');
  }, []);

  const temExportacao = Boolean(nomeArquivo) && colunas.some((c) => c.exportar);

  // A largura maxima vem de `--gps-pagina-max`, publicada pelo shell conforme a
  // opcao "Largura" do painel de aparencia (`none` em total, 1560px em limitada).
  //
  // COMENTARIO EM `//` E NAO EM `{/* */}`: chave dentro de `return (` antes do
  // elemento raiz nao e comentario JSX, e sim uma expressao - o parser le como
  // objeto e o build morre com "Expected `,` or `)`". Comentario JSX so vale
  // ENTRE filhos de um elemento.
  return (
    <div className="mx-auto w-full max-w-[var(--gps-pagina-max)] p-6 font-sans text-ink">
      <div className="mb-1.5 flex flex-wrap items-end justify-between gap-3">
        <div>
          {/* A descricao virou DICA no icone ao lado do titulo, em vez de duas ou
              tres linhas impressas: somada ao cabecalho, aos indicadores e aos
              filtros, ela empurrava a lista - que e o motivo de a tela existir -
              para fora da primeira tela. Ver TituloTela. */}
          <TituloTela descricao={descricao}>{titulo}</TituloTela>
        </div>
        {acao}
      </div>

      {/* ------------------------------------------------------------------
          FAIXA DE INDICADORES - um cartao so, dividido, em vez de N cartoes.

          Historico curto e util: eram N cartoes soltos num grid. Em HD com escala
          de 125% do Windows (~1093px CSS) tres cartoes nao caberam e o terceiro
          era CORTADO. A primeira tentativa foi baixar a quebra para `xl`, e isso
          piorou: os tres viravam 2 + 1, gastando uma linha inteira da tela para
          um cartao sozinho.

          Um cartao dividido resolve os dois de uma vez. Ele economiza as N-1
          molduras e os N-1 espacos entre cartoes - e essa largura devolvida e
          exatamente o que faltava para os tres caberem lado a lado. Como bonus,
          numeros que se comparam ("total / ativos / perdidos" sao a mesma
          grandeza) ficam dentro da MESMA moldura, que e o que diz ao olho que eles
          formam um conjunto.

          `divide-x` no lugar de borda por item: uma linha entre segmentos, sem
          dobrar espessura no encontro de dois.
          ------------------------------------------------------------------ */}
      {!!indicadores?.length && (
        <div className="mb-1.5 overflow-hidden rounded-lg border border-line bg-surface shadow-card">
          <div
            className={cn(
              'grid divide-line',
              // Empilhado no celular; a partir de `sm` divide na horizontal, e ai
              // as divisorias passam a ser verticais.
              'divide-y sm:divide-x sm:divide-y-0',
              /* Classes ESTATICAS, uma por contagem. `sm:grid-cols-${n}` seria
                 montada em tempo de execucao, e o Tailwind so gera CSS para o que
                 le no codigo-fonte: a classe existiria no HTML sem nenhuma regra
                 por tras - a faixa ficaria com tudo numa coluna, sem erro nenhum
                 no build. */
              COLUNAS_FAIXA[Math.min(indicadores.length, 5)]
            )}
          >
            {indicadores.map((ind) => (
              <CartaoIndicador
                key={ind.rotulo}
                rotulo={ind.rotulo}
                valor={ind.valor.toLocaleString('pt-BR')}
                legenda={ind.legenda}
                tom={ind.tom}
                emFaixa
                // "% da base" so aparece quando ha base real - percentual sobre zero
                // e sobre o proprio total nao informa nada.
                delta={
                  ind.base && ind.base > 0 && ind.valor !== ind.base
                    ? `${Math.round((ind.valor / ind.base) * 100)}% da base`
                    : undefined
                }
                proporcao={ind.base && ind.base > 0 ? ind.valor / ind.base : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {(aoBuscar || segmentos || filtrosExtras) && (
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
          {aoBuscar && (
            <CaixaBusca
              valor={busca ?? ''}
              aoBuscar={aoBuscar}
              placeholder={placeholderBusca}
              /* ENCOLHE em vez de forcar quebra de linha: base de 320px, pode ir
                 ate 160px, para "Limpar filtros" e "Excel" nao cairem para uma
                 segunda linha em HD. */
              className="min-w-[160px] flex-1 basis-[320px] sm:max-w-[320px]"
            />
          )}
          {segmentos && aoTrocarSegmento && typeof segmentoAtual === 'number' && (
            <FiltroSegmentado opcoes={segmentos} valor={segmentoAtual} onChange={aoTrocarSegmento} />
          )}
          {filtrosExtras}

          {/* Alinhados a direita: agem sobre a lista, nao a filtram. */}
          <span className="ml-auto flex items-center gap-2">
            {aoLimparFiltros && (
              <button
                type="button"
                onClick={aoLimparFiltros}
                className="h-9 rounded-lg border border-line bg-surface px-3 text-[12px] font-semibold text-muted-foreground hover:text-ink"
                title="Voltar todos os filtros ao padrão"
              >
                Limpar filtros
              </button>
            )}

            {temExportacao && (
              <button
                type="button"
                onClick={exportar}
                disabled={exportando || !linhas.length}
                /* SO O ICONE, sem a palavra "Excel" - pedido de 26/08/2026, para o
                   "Limpar filtros" caber na mesma linha em tela estreita.

                   Da para tirar a palavra porque o icone de planilha e reconhecido
                   e o `title` continua dizendo exatamente o que o botao faz ("Baixa
                   em .xlsx as linhas desta pagina"). O `aria-label` mantem o botao
                   nomeado para leitor de tela - icone sozinho sem nome acessivel
                   seria um botao sem rotulo.

                   Enquanto GERA, o texto volta: ai ele nao e rotulo, e estado, e
                   um botao que nao responde por dois segundos sem dizer nada
                   parece travado. */
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-line bg-surface px-2.5 text-[12px] font-semibold text-muted-foreground hover:text-ink disabled:opacity-40"
                title="Baixa em .xlsx as linhas desta página"
                aria-label="Exportar para Excel"
              >
                <IconePlanilha />
                {exportando && <span>Gerando...</span>}
              </button>
            )}
          </span>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-primary/25 bg-surface shadow-card">
        <div className="overflow-x-auto">
          <div className="min-w-[880px]">
            {/* CABECALHO.

                A faixa e `bg-primary/10` com titulo em `text-primary`: ela pega a
                cor da paleta escolhida em Aparencia, entao muda junto com o tema
                em vez de ficar num cinza que ignora a preferencia. Antes era
                `bg-soft`, e antes disso `bg-surface-2` (#fafbfc), que e quase o
                mesmo branco do corpo - na pratica a faixa nao existia, e com muitas
                colunas o olho perdia a linha que separa titulo de dado.

                A opacidade e /10 e nao /20: cabecalho e moldura, nao conteudo. Em
                /20 a faixa competia com os dados da primeira linha.

                DEFEITO CORRIGIDO - TITULOS SE SOBREPONDO: cada celula tinha
                `min-w-0` (permite encolher) mas NAO `overflow-hidden`, e o
                `truncate` estava no <button>, um `inline-flex`. Num flex, o filho
                nao encolhe abaixo do proprio min-content a menos que ele tambem
                receba `min-w-0` - entao o texto vazava para fora da coluna e
                invadia a vizinha. Era visivel em rotulo longo com coluna estreita,
                como "ULTIMA SINCRONIZACAO" ao lado de "NOME", na lista de
                dispositivos.

                O `title` leva o rotulo COMPLETO: se o titulo vai ser cortado, o
                texto inteiro precisa continuar alcancavel em algum lugar. */}
            <div
              className={CABECALHO_TABELA}
              style={{ gridTemplateColumns: grade }}
            >
              {colunas.map((c) => {
                const podeOrdenar = Boolean(c.ordenarPor && aoOrdenar);
                const ativa = Boolean(c.ordenarPor && c.ordenarPor === ordenarPor);
                return (
                  <div
                    key={c.chave}
                    /* 🔴 DEFEITO CORRIGIDO (segunda tentativa) - "DISPOSITIVOSSTATUS".
                       Duas colunas vizinhas encostavam os titulos e liam como uma
                       palavra so.

                       A PRIMEIRA TENTATIVA ERROU O LADO: pus `pl-3` na coluna
                       alinhada a direita e `pr-3` na alinhada a esquerda. So que o
                       problema esta no ENCONTRO das duas: o titulo alinhado a
                       direita termina no limite direito da propria coluna, e o
                       titulo seguinte, alinhado a esquerda, comeca no limite
                       esquerdo da dele - que e o mesmo ponto. Padding do lado
                       oposto ao encontro nao separa nada, e foi por isso que
                       "DISPOSITIVOSSTATUS" continuou colado no print seguinte.

                       Agora e `px-2` nas DUAS bordas, no cabecalho E nas linhas de
                       dados - o mesmo recuo dos dois lados garante o respiro
                       qualquer que seja o alinhamento, e como padding nao muda a
                       largura das trilhas do grid, cabecalho e dados continuam
                       alinhados coluna a coluna. (`gap` no grid resolveria o
                       espaco, mas mudaria as trilhas e desalinharia os dois.) */
                    className={cn(
                      'min-w-0 overflow-hidden px-2',
                      c.alinhar === 'dir' && 'text-right'
                    )}
                  >
                    {podeOrdenar ? (
                      <button
                        type="button"
                        onClick={() => aoOrdenar!(c.ordenarPor!)}
                        className={cn(
                          /* 🔴 CORRIGIDO: aqui havia `ativa && 'text-ink'`. Com o
                             cabecalho na cor cheia da paleta (azul-marinho), `text-ink`
                             e texto QUASE PRETO sobre fundo escuro - a coluna ordenada
                             ficava a menos legivel de todas, justo a que importa. Numa
                             faixa escura o destaque tem que ser mais CLARO, nao mais
                             escuro: o inativo fica levemente rebaixado e o ativo em
                             cor cheia com sublinhado. */
                          'inline-flex w-full items-center gap-1 overflow-hidden uppercase tracking-[0.07em] hover:underline',
                          c.alinhar === 'dir' && 'justify-end',
                          ativa ? 'underline underline-offset-2' : 'opacity-80 hover:opacity-100'
                        )}
                        title={`${c.rotulo} — ordenar por esta coluna`}
                      >
                        <span className="min-w-0 truncate">{c.rotulo}</span>
                        {ativa && <span className="flex-none">{direcao === 'asc' ? '▲' : '▼'}</span>}
                      </button>
                    ) : (
                      <span className="block truncate" title={c.rotulo}>
                        {c.rotulo}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {carregando &&
              Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="grid items-center border-b px-3"
                  style={{ gridTemplateColumns: grade, borderColor: 'var(--gps-soft)', minHeight: 'var(--gps-row-h)' }}
                >
                  {colunas.map((c) => (
                    <div
                      key={c.chave}
                      className={cn('min-w-0 px-2', c.alinhar === 'dir' && 'flex justify-end')}
                    >
                      {c.esqueleto === 'nenhum' ? null : <Skeleton className={cn('h-3', c.esqueleto || 'w-24')} />}
                    </div>
                  ))}
                </div>
              ))}

            {!carregando && erro && (
              <div className="p-8 text-center text-[12.5px] text-danger">
                {erro}
                {aoRecarregar && (
                  <button
                    type="button"
                    onClick={aoRecarregar}
                    className="ml-2 font-semibold text-primary underline underline-offset-2"
                  >
                    tentar de novo
                  </button>
                )}
              </div>
            )}

            {!carregando && !erro && linhas.length === 0 && (
              <div className="p-8 text-center text-[12.5px] text-muted-foreground">{vazio}</div>
            )}

            {!carregando &&
              !erro &&
              linhas.map((linha, i) => (
                <div
                  key={chaveLinha(linha, i)}
                  onClick={aoClicarLinha ? () => aoClicarLinha(linha) : undefined}
                  className={cn(
                    'grid items-center border-b px-3 hover:bg-surface-2',
                    aoClicarLinha && 'cursor-pointer'
                  )}
                  style={{ gridTemplateColumns: grade, borderColor: 'var(--gps-soft)', minHeight: 'var(--gps-row-h)' }}
                >
                  {colunas.map((c) => (
                    <div
                      /* `min-w-0` e o que permite a celula ENCOLHER. Item de grid tem
                         largura minima automatica igual ao conteudo: sem isto a celula
                         estufa com o texto, empurra a vizinha e o texto de uma coluna
                         aparece em cima da outra - foi o que apareceu em Grupos, com a
                         descricao invadindo a regional. Nenhum `truncate` interno
                         funciona enquanto a celula puder crescer. */
                      // `px-2` igual ao do cabecalho: o recuo tem que ser o MESMO nos
                      // dois, senao o titulo deixa de ficar em cima do dado que ele
                      // nomeia. Aqui ele tambem separa valor de valor - era o caso do
                      // "121" encostando na pilha ATIVO na lista de grupos.
                      key={c.chave}
                      className={cn('min-w-0 px-2', c.alinhar === 'dir' && 'text-right')}
                    >
                      {c.render(linha, i)}
                    </div>
                  ))}
                </div>
              ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-3 py-2 pr-16 text-[12px] text-muted-foreground">
          <span className="flex flex-wrap items-center gap-2">
            <span>
              Mostrando <Num>{linhas.length}</Num> de <Num>{total.toLocaleString('pt-BR')}</Num>{' '}
              {rotuloPlural}
            </span>

            {aoTrocarLinhasPorPagina && typeof linhasPorPagina === 'number' && (
              <label className="flex items-center gap-1.5">
                <span className="text-faint">por página</span>
                <select
                  value={linhasPorPagina}
                  onChange={(e) => aoTrocarLinhasPorPagina(Number(e.target.value))}
                  className="h-7 rounded-md border border-line bg-surface px-1.5 text-[12px] tabular-nums outline-none focus:border-primary"
                >
                  {OPCOES_LINHAS.map((n) => (
                    <option key={n} value={n}>
                      {rotuloLinhas(n)}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={pagina === 0}
              onClick={() => aoTrocarPagina(Math.max(0, pagina - 1))}
              className="h-7 rounded-md border border-line px-2 font-semibold disabled:opacity-40"
            >
              Anterior
            </button>
            <span>
              <Num>{pagina + 1}</Num> / <Num>{totalPaginas}</Num>
            </span>
            <button
              type="button"
              disabled={pagina + 1 >= totalPaginas}
              onClick={() => aoTrocarPagina(pagina + 1)}
              className="h-7 rounded-md border border-line px-2 font-semibold disabled:opacity-40"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
